import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminPageTitle,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  LIMITS_ROUTE,
  buildSampleLimitRules,
  formatConditionsPreview,
  formatLimitDisplay,
  getLimitForLabel,
  getLimitTypeLabel,
  getRequirementLabel,
  getUnitLabel,
  normalizeLimitForm,
  sortLimitRules,
} from '../../../data/limitsThresholdsConfig';

const TABLE_COLUMNS = [
  'Rule ID',
  'Rule Name',
  'Requirement',
  'Limit For',
  'Limit Type',
  'Limit',
  'Unit',
  'Conditions',
  'Actions',
];

const TABLE_GRID =
  'minmax(0, 0.5fr) minmax(0, 1.05fr) minmax(0, 0.85fr) minmax(0, 0.95fr) minmax(0, 0.75fr) minmax(0, 1fr) minmax(0, 0.45fr) minmax(0, 1fr) minmax(62px, 0.04fr)';

const LimitsThresholds = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState(() => sortLimitRules(buildSampleLimitRules()));

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(rules);

  useEffect(() => {
    const incoming = location.state?.rules;
    if (Array.isArray(incoming)) {
      setRules(sortLimitRules(incoming.map(normalizeLimitForm)));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const openRuleView = (rule) => {
    navigate(`${LIMITS_ROUTE}/${urlNameSegment(rule.ruleName)}`, {
      state: { rule, viewMode: true, existingRules: rules },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Limits & Thresholds</AdminPageTitle>
          <AdminHeaderAction
            onClick={() => navigate(`${LIMITS_ROUTE}/add`, { state: { existingRules: rules } })}
          >
            Add Rule
          </AdminHeaderAction>
        </div>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: TABLE_GRID }}
            >
              {TABLE_COLUMNS.map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0${label === 'Actions' ? ' shrink-0 justify-center' : ''}`}
                >
                  <AdminTableHeader className={label === 'Actions' ? '' : 'truncate'}>{label}</AdminTableHeader>
                </div>
              ))}
            </div>

            {paginatedRules.map((rule) => {
              const normalized = normalizeLimitForm(rule);
              const conditionsPreview = formatConditionsPreview(normalized.conditions);
              return (
                <div
                  key={normalized.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRuleView(normalized)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openRuleView(normalized);
                    }
                  }}
                  className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                  style={{ gridTemplateColumns: TABLE_GRID }}
                >
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableMeta className="truncate block" title={normalized.id}>
                      {normalized.id}
                    </AdminTableMeta>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={normalized.ruleName}>
                      {normalized.ruleName}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {getRequirementLabel(normalized.requirement)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {getLimitForLabel(normalized.limitFor)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {getLimitTypeLabel(normalized.limitType)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={formatLimitDisplay(normalized)}>
                      {formatLimitDisplay(normalized)}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{getUnitLabel(normalized.unit)}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={conditionsPreview.lines.join(', ') || conditionsPreview.text}>
                      {conditionsPreview.text}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center justify-center shrink-0 overflow-visible" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[normalized.id] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowActions(normalized.id);
                        }}
                        aria-expanded={!!rowActionsOpen[normalized.id]}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {rowActionsOpen[normalized.id] && rowActionsMenuLayout && (
                        <AdminFixedRowActionsMenu
                          layout={rowActionsMenuLayout}
                          menuRef={rowActionsMenuRef}
                          placementReady={rowActionsMenuPlacementReady}
                        >
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              closeRowActions();
                              openRuleView(normalized);
                            }}
                          >
                            View
                          </AdminRowMenuItem>
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              closeRowActions();
                              navigate(`${LIMITS_ROUTE}/${urlNameSegment(normalized.ruleName)}/edit`, {
                                state: { rule: normalized, existingRules: rules },
                              });
                            }}
                          >
                            Edit
                          </AdminRowMenuItem>
                        </AdminFixedRowActionsMenu>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default LimitsThresholds;
